import { useState, useEffect, useMemo } from 'react'
import { X, ChevronDown, ExternalLink, Info, Rocket } from 'lucide-react'
import { supabase } from './supabase'
import { useToast } from './ToastContext'
import {
  SERVICES, AD_PLATFORMS, visibleQuestions, computeRecommendations,
  SERVICE_TAG_TO_AREA, WEEK_TO_NUMBER, SORT_ORDER, REVIEW_SECTIONS, sortByExecutionOrder
} from './kickoffConfig'
import { isActiveProfile } from './constants'

const STEPS = ['Services', 'Universal', 'Specifics', 'Review & Assign']

export default function KickoffBuilder({ client, currentUser, profiles, onClose, onSaved }) {
  const toast = useToast()
  const [kickoff, setKickoff] = useState(null)
  const [templates, setTemplates] = useState([])
  const [existingTemplateIds, setExistingTemplateIds] = useState(new Set())
  const [answers, setAnswers] = useState({})
  const [step, setStep] = useState(1)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [selected, setSelected] = useState(new Set())
  const [expanded, setExpanded] = useState(new Set())

  // Per-task assignment state
  const [assignments, setAssignments] = useState({})
  const [bulkAssignedTo, setBulkAssignedTo] = useState('')
  const [bulkDateDue, setBulkDateDue] = useState('')
  const [bulkWeek, setBulkWeek] = useState('')

  // Load
  useEffect(() => {
    let mounted = true
    ;(async () => {
      const { data: existing } = await supabase.from('kickoffs').select('*').eq('client_id', client.id).maybeSingle()
      let k = existing
      if (!k) {
        const { data: created, error } = await supabase.from('kickoffs').insert({
          client_id: client.id, status: 'draft', created_by: currentUser.id, answers: {},
        }).select().single()
        if (error) { toast('Error starting kickoff: ' + error.message, 'error'); return }
        k = created
      }
      const { data: tpls } = await supabase.from('task_templates').select('*')
      const { data: priorTasks } = await supabase.from('tasks').select('template_id').eq('kickoff_id', k.id)

      if (!mounted) return
      setKickoff(k)
      setAnswers(k.answers || {})
      setTemplates(tpls || [])
      setExistingTemplateIds(new Set((priorTasks || []).map(t => t.template_id).filter(Boolean)))
      setLoading(false)
    })()
    return () => { mounted = false }
  }, [client.id, currentUser.id, toast])

  // Auto-save answers (debounced)
  useEffect(() => {
    if (!kickoff) return
    const t = setTimeout(() => {
      supabase.from('kickoffs').update({ answers, updated_at: new Date().toISOString() }).eq('id', kickoff.id)
    }, 400)
    return () => clearTimeout(t)
  }, [answers, kickoff])

  const set = (k, v) => setAnswers(a => ({ ...a, [k]: v }))
  const toggleArr = (key, val) => setAnswers(a => {
    const cur = a[key] || []
    return { ...a, [key]: cur.includes(val) ? cur.filter(x => x !== val) : [...cur, val] }
  })

  const services = answers.services || []
  const adPlatforms = answers.ad_platforms || []
  const visible = useMemo(() => visibleQuestions(answers), [answers])
  const allRecs = useMemo(() => computeRecommendations(answers), [answers])
  const newRecs = useMemo(() => allRecs.filter(r => !existingTemplateIds.has(r.template_id)), [allRecs, existingTemplateIds])

  // Auto-select all new recs when arriving at Review
  useEffect(() => {
    if (step === 4) setSelected(new Set(newRecs.map(r => r.template_id)))
  }, [step, newRecs])

  const isRerun = existingTemplateIds.size > 0
  const step1Valid = services.length > 0 && (!services.includes('ads') || adPlatforms.length > 0)

  // Enrich + group by service area, sorted by execution order
  const enrichedRecs = useMemo(() => {
    return newRecs
      .map(r => ({ ...r, template: templates.find(t => t.id === r.template_id) }))
      .filter(r => r.template)
  }, [newRecs, templates])

  // Group recommendations by service area (using SERVICE_TAG_TO_AREA on first tag)
  const groupedRecs = useMemo(() => {
    const groups = {}
    for (const section of REVIEW_SECTIONS) groups[section.key ?? '__universal__'] = []
    for (const rec of enrichedRecs) {
      const firstTag = (rec.template.service_tags || [])[0]
      const area = SERVICE_TAG_TO_AREA[firstTag] || null
      const key = area ?? '__universal__'
      if (!groups[key]) groups[key] = []
      groups[key].push(rec)
    }
    // Sort each group by SORT_ORDER
    for (const k of Object.keys(groups)) {
      groups[k] = sortByExecutionOrder(groups[k], r => r.template)
    }
    return groups
  }, [enrichedRecs])

  // Initialize assignments when entering Review
  useEffect(() => {
    if (step !== 4) return
    setAssignments(prev => {
      const next = { ...prev }
      for (const rec of enrichedRecs) {
        if (!next[rec.template_id]) {
          next[rec.template_id] = {
            assigned_to: '',
            date_due: '',
            notes: '',
            week_number: WEEK_TO_NUMBER[rec.template.default_week] || '',
          }
        }
      }
      return next
    })
  }, [step, enrichedRecs])

  const toggleSel = id => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n })
  const toggleExp = id => setExpanded(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n })

  const updateAssignment = (templateId, field, value) => {
    setAssignments(prev => ({
      ...prev,
      [templateId]: { ...(prev[templateId] || {}), [field]: value }
    }))
  }

  const applyToAll = () => {
    setAssignments(prev => {
      const next = { ...prev }
      for (const rec of enrichedRecs) {
        if (!selected.has(rec.template_id)) continue
        next[rec.template_id] = {
          ...(next[rec.template_id] || {}),
          assigned_to: bulkAssignedTo || next[rec.template_id]?.assigned_to || '',
          date_due: bulkDateDue || next[rec.template_id]?.date_due || '',
          week_number: bulkWeek !== '' ? bulkWeek : (next[rec.template_id]?.week_number || ''),
        }
      }
      return next
    })
    toast('Applied to selected tasks', 'success')
  }

  const addTasks = async () => {
    if (selected.size === 0) return
    setSaving(true)
    try {
      const rows = []
      for (const rec of enrichedRecs) {
        if (!selected.has(rec.template_id)) continue
        const tpl = rec.template
        const a = assignments[tpl.id] || {}
        const tag = (tpl.service_tags || [])[0]
        const service_area = SERVICE_TAG_TO_AREA[tag] || null
        const week_number = a.week_number !== '' && a.week_number !== undefined && a.week_number !== null
          ? parseInt(a.week_number, 10)
          : (WEEK_TO_NUMBER[tpl.default_week] || null)
        rows.push({
          title: tpl.title,
          description: tpl.description || '',
          notes: a.notes || null,
          client_id: client.id,
          kickoff_id: kickoff.id,
          template_id: tpl.id,
          assigned_to: a.assigned_to || null,
          assigned_by: currentUser.id,
          date_due: a.date_due || null,
          week_number,
          status: 'not_started',
          service_area,
          approval_path: tpl.approval_path || 'internal',
          is_onboarding_task: true,
          client_visible: false,
          collaborator_ids: [],
          triggered_by: { template_id: tpl.id, reason: rec.reason },
          checklist_items: (tpl.checklist_items || []).map(it => ({ ...it, checked: false })),
        })
      }
      const { error } = await supabase.from('tasks').insert(rows)
      if (error) throw error

      // If the website build checklist was added, also set the client's website_mode
      // so the Website Workspace is automatically launched for them.
      const websiteBuildAdded = rows.some(r => r.template_id === 'std-website-build-checklist')
      if (websiteBuildAdded) {
        const mode = answers.website_status === 'New website' ? 'new_build' : 'audit_rebuild'
        await supabase.from('clients').update({ website_mode: mode }).eq('id', client.id)
      }

      await supabase.from('kickoffs').update({
        status: 'active',
        answers,
        updated_at: new Date().toISOString(),
      }).eq('id', kickoff.id)
      toast(`Added ${rows.length} task${rows.length === 1 ? '' : 's'} to ${client.name}`, 'success')
      onSaved && onSaved()
    } catch (e) {
      toast('Error: ' + e.message, 'error')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="modal-overlay">
        <div className="modal" style={{ maxWidth: 480, padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
          Loading kickoff…
        </div>
      </div>
    )
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: step === 4 ? 900 : 720, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <div>
            <div className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Rocket size={18} color="var(--gold)" />
              Kickoff Builder — {client.name}
            </div>
            <div style={{ display: 'flex', gap: 4, marginTop: 8, flexWrap: 'wrap' }}>
              {STEPS.map((s, i) => {
                const disabled = (i + 1 === 4 && !step1Valid)
                return (
                  <button key={i} onClick={() => !disabled && setStep(i + 1)} disabled={disabled}
                    style={{
                      padding: '3px 10px', borderRadius: 50, fontSize: 11, border: 'none',
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      fontWeight: step === i + 1 ? 700 : 400,
                      background: step === i + 1 ? 'var(--navy)' : 'var(--cream-dark)',
                      color: step === i + 1 ? '#fff' : 'var(--text-muted)',
                      opacity: disabled ? 0.4 : 1,
                    }}>
                    {i + 1}. {s}
                  </button>
                )
              })}
            </div>
          </div>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>

        <div className="modal-body" style={{ overflowY: 'auto', flex: 1 }}>
          {step === 1 && <StepServices answers={answers} toggleArr={toggleArr} />}
          {step === 2 && <StepUniversal answers={answers} set={set} questions={visible.universal} />}
          {step === 3 && <StepSpecifics answers={answers} set={set} toggleArr={toggleArr} visible={visible} />}
          {step === 4 && (
            <StepReviewAndAssign
              isRerun={isRerun}
              groupedRecs={groupedRecs}
              selected={selected}
              expanded={expanded}
              toggleSel={toggleSel}
              toggleExp={toggleExp}
              assignments={assignments}
              profiles={profiles || []}
              bulkAssignedTo={bulkAssignedTo}
              setBulkAssignedTo={setBulkAssignedTo}
              bulkDateDue={bulkDateDue}
              setBulkDateDue={setBulkDateDue}
              bulkWeek={bulkWeek}
              setBulkWeek={setBulkWeek}
              applyToAll={applyToAll}
              updateAssignment={updateAssignment}
            />
          )}
        </div>

        <div className="modal-footer">
          {step > 1 && <button className="btn btn-ghost" onClick={() => setStep(s => s - 1)}>← Back</button>}
          <div style={{ marginRight: 'auto', fontSize: 12, color: 'var(--text-muted)' }}>
            Step {step} of {STEPS.length}
            {isRerun && step === 4 && <span style={{ marginLeft: 8, color: 'var(--mauve)', fontWeight: 500 }}>· Re-run mode</span>}
          </div>
          {step < 4 && (
            <button className="btn btn-gold" onClick={() => setStep(s => s + 1)} disabled={step === 1 && !step1Valid}>
              Next →
            </button>
          )}
          {step === 4 && (
            <button className="btn btn-gold" onClick={addTasks} disabled={saving || selected.size === 0}>
              {saving ? 'Creating…' : `🚀 Create ${selected.size} task${selected.size === 1 ? '' : 's'}`}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Step 1: Services ────────────────────────────────────────
function StepServices({ answers, toggleArr }) {
  const services = answers.services || []
  const adPlatforms = answers.ad_platforms || []
  return (
    <>
      <div style={{ fontFamily: 'var(--font-display)', fontSize: 15, color: 'var(--navy)', marginBottom: 6 }}>
        Which services is this client signing up for?
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>Select at least one.</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8, marginBottom: 20 }}>
        {SERVICES.map(s => <ToggleCard key={s.id} active={services.includes(s.id)} onClick={() => toggleArr('services', s.id)}>{s.label}</ToggleCard>)}
      </div>
      {services.includes('ads') && (
        <>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 15, color: 'var(--navy)', marginBottom: 6 }}>
            Which ad platforms?
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>Select at least one.</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8 }}>
            {AD_PLATFORMS.map(p => <ToggleCard key={p.id} active={adPlatforms.includes(p.id)} onClick={() => toggleArr('ad_platforms', p.id)}>{p.label}</ToggleCard>)}
          </div>
        </>
      )}
    </>
  )
}

// ─── Step 2: Universal ───────────────────────────────────────
function StepUniversal({ answers, set, questions }) {
  return (
    <>
      <div style={{ fontFamily: 'var(--font-display)', fontSize: 15, color: 'var(--navy)', marginBottom: 14 }}>
        Universal context
      </div>
      <div style={{ display: 'grid', gap: 18 }}>
        {questions.map(q => <QuestionRow key={q.id} q={q} answers={answers} set={set} />)}
      </div>
    </>
  )
}

// ─── Step 3: Specifics ───────────────────────────────────────
function StepSpecifics({ answers, set, toggleArr, visible }) {
  const sections = [
    { key: 'website', label: 'Website' },
    { key: 'seo', label: 'SEO' },
    { key: 'social', label: 'Social' },
    { key: 'ads', label: 'Ads' },
    { key: 'email', label: 'Email / Newsletters' },
  ].filter(s => visible[s.key].length > 0)

  if (sections.length === 0) {
    return <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No service-specific questions for the services you selected. Continue to review.</p>
  }

  return (
    <div style={{ display: 'grid', gap: 24 }}>
      {sections.map(s => (
        <div key={s.key}>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 15, color: 'var(--navy)', marginBottom: 12, paddingBottom: 6, borderBottom: '1px solid var(--border-light)' }}>
            {s.label}
          </div>
          <div style={{ display: 'grid', gap: 16 }}>
            {visible[s.key].map(q => <QuestionRow key={q.id} q={q} answers={answers} set={set} toggleArr={toggleArr} />)}
          </div>
        </div>
      ))}
    </div>
  )
}

function QuestionRow({ q, answers, set, toggleArr }) {
  const val = answers[q.id]
  return (
    <div>
      <div style={{ fontWeight: 500, fontSize: 13.5, marginBottom: 8 }}>{q.text}</div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {q.options.map(opt => {
          const active = q.type === 'multi' ? (val || []).includes(opt) : val === opt
          return (
            <ToggleChip key={opt} active={active}
              onClick={() => q.type === 'multi' ? toggleArr(q.id, opt) : set(q.id, opt)}>
              {opt}
            </ToggleChip>
          )
        })}
      </div>
    </div>
  )
}

// ─── Step 4: Review & Assign (combined) ──────────────────────
function StepReviewAndAssign({
  isRerun, groupedRecs, selected, expanded, toggleSel, toggleExp,
  assignments, profiles, bulkAssignedTo, setBulkAssignedTo,
  bulkDateDue, setBulkDateDue, bulkWeek, setBulkWeek, applyToAll, updateAssignment
}) {
  const total = Object.values(groupedRecs).flat().length

  return (
    <>
      {isRerun && (
        <div style={{ background: 'var(--blush)', border: '1px solid var(--mauve)', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 12.5, color: 'var(--mauve)', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <Info size={14} style={{ flexShrink: 0, marginTop: 2 }} />
          <span>Re-running the questionnaire. Only <strong>new</strong> recommendations (not already added to this kickoff) are shown.</span>
        </div>
      )}

      {total === 0 ? (
        <div className="empty-state" style={{ padding: '32px 12px' }}>
          <h3>{isRerun ? 'No new recommendations' : 'No tasks recommended yet'}</h3>
          <p>{isRerun ? 'Based on the updated answers, nothing new to add.' : 'Go back and review your answers.'}</p>
        </div>
      ) : (
        <>
          {/* Apply to all */}
          <div style={{ background: 'var(--cream)', borderRadius: 12, padding: 14, marginBottom: 18, position: 'sticky', top: 0, zIndex: 5 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>
              Apply to all selected tasks
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 80px auto', gap: 8, alignItems: 'flex-end' }}>
              <div>
                <label className="form-label" style={{ fontSize: 11 }}>Assignee</label>
                <select className="form-control" value={bulkAssignedTo} onChange={e => setBulkAssignedTo(e.target.value)}>
                  <option value="">— select —</option>
                  {profiles.filter(isActiveProfile).map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
                </select>
              </div>
              <div>
                <label className="form-label" style={{ fontSize: 11 }}>Due Date</label>
                <input className="form-control" type="date" value={bulkDateDue} onChange={e => setBulkDateDue(e.target.value)} />
              </div>
              <div>
                <label className="form-label" style={{ fontSize: 11 }}>Week #</label>
                <input className="form-control" type="number" min="1" value={bulkWeek} onChange={e => setBulkWeek(e.target.value)} placeholder="1" />
              </div>
              <button className="btn btn-gold btn-sm" onClick={applyToAll}>Apply ↓</button>
            </div>
          </div>

          {/* Grouped sections */}
          {REVIEW_SECTIONS.map(section => {
            const key = section.key ?? '__universal__'
            const recs = groupedRecs[key] || []
            if (recs.length === 0) return null
            return (
              <div key={key} style={{ marginBottom: 22 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 10, paddingBottom: 6, borderBottom: '1.5px solid var(--border-light)' }}>
                  {section.label} <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>({recs.length})</span>
                </div>
                <div style={{ display: 'grid', gap: 8 }}>
                  {recs.map(rec => (
                    <InlineRecRow
                      key={rec.template_id}
                      rec={rec}
                      selected={selected.has(rec.template_id)}
                      expanded={expanded.has(rec.template_id)}
                      assignment={assignments[rec.template_id] || {}}
                      profiles={profiles}
                      onToggleSel={() => toggleSel(rec.template_id)}
                      onToggleExp={() => toggleExp(rec.template_id)}
                      onUpdate={(field, value) => updateAssignment(rec.template_id, field, value)}
                    />
                  ))}
                </div>
              </div>
            )
          })}
        </>
      )}
    </>
  )
}

function InlineRecRow({ rec, selected, expanded, assignment, profiles, onToggleSel, onToggleExp, onUpdate }) {
  const tpl = rec.template
  const isChecklist = tpl.task_type === 'checklist'

  return (
    <div style={{
      background: '#fff',
      border: `1.5px solid ${selected ? 'var(--gold)' : 'var(--border-light)'}`,
      borderRadius: 10,
      padding: '10px 12px',
      opacity: selected ? 1 : 0.55,
      transition: 'opacity 0.15s, border-color 0.15s',
    }}>
      {/* Title row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: selected ? 10 : 0 }}>
        <input type="checkbox" checked={selected} onChange={onToggleSel}
          style={{ marginTop: 3, width: 15, height: 15, cursor: 'pointer', flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 13.5, marginBottom: 4 }}>
            {tpl.title}
            {tpl.approval_path === 'internal_client_live' && (
              <span style={{ marginLeft: 8, fontSize: 10, padding: '1px 7px', borderRadius: 50, background: '#f0eef8', color: 'var(--mauve)', fontWeight: 600 }}>→ Client approves</span>
            )}
            {tpl.approval_path === 'internal_then_client_fyi' && (
              <span style={{ marginLeft: 8, fontSize: 10, padding: '1px 7px', borderRadius: 50, background: '#e8f5fa', color: '#2a6e84', fontWeight: 600 }}>→ Client FYI</span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            {tpl.default_week && (
              <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 50, background: 'var(--cream-dark)', color: 'var(--text-muted)' }}>{tpl.default_week} target</span>
            )}
            {isChecklist && (
              <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 50, background: 'var(--cream-dark)', color: 'var(--text-secondary)', fontWeight: 500 }}>
                Checklist · {(tpl.checklist_items || []).length} items
              </span>
            )}
            <span title={rec.reason} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, color: 'var(--text-muted)', cursor: 'help', marginLeft: 'auto' }}>
              <Info size={11} /> {rec.reason}
            </span>
          </div>
        </div>
        {isChecklist && (
          <button onClick={onToggleExp} className="btn-icon" style={{ flexShrink: 0 }} title={expanded ? 'Hide preview' : 'Show preview'}>
            <ChevronDown size={14} style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
          </button>
        )}
      </div>

      {/* Inline assignment fields (only when selected) */}
      {selected && (
        <div style={{ paddingLeft: 25 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 70px', gap: 8, marginBottom: 6 }}>
            <select className="form-control" value={assignment.assigned_to || ''} onChange={e => onUpdate('assigned_to', e.target.value)} style={{ fontSize: 12.5, padding: '6px 8px' }}>
              <option value="">Unassigned</option>
              {profiles.filter(isActiveProfile).map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
            </select>
            <input className="form-control" type="date" value={assignment.date_due || ''} onChange={e => onUpdate('date_due', e.target.value)} style={{ fontSize: 12.5, padding: '6px 8px' }} />
            <input className="form-control" type="number" min="1" value={assignment.week_number ?? ''} onChange={e => onUpdate('week_number', e.target.value)} placeholder="Wk" style={{ fontSize: 12.5, padding: '6px 8px' }} />
          </div>
          <input className="form-control" value={assignment.notes || ''} onChange={e => onUpdate('notes', e.target.value)} placeholder="Notes (optional)..." style={{ fontSize: 12.5, padding: '6px 8px' }} />
        </div>
      )}

      {/* Checklist preview */}
      {expanded && isChecklist && (
        <div style={{ marginTop: 10, paddingTop: 10, paddingLeft: 25, borderTop: '1px solid var(--border-light)' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>
            Checklist preview
          </div>
          {(tpl.checklist_items || []).map((it, i) => (
            <div key={i} style={{ fontSize: 12.5, color: 'var(--text-secondary)', padding: '3px 0', display: 'flex', gap: 6, alignItems: 'flex-start' }}>
              <span style={{ color: 'var(--text-muted)' }}>·</span>
              <span style={{ flex: 1 }}>
                {it.text}
                {it.doc_link && (
                  <a href={it.doc_link} target="_blank" rel="noreferrer"
                    style={{ marginLeft: 6, color: 'var(--teal)', display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: 11 }}>
                    <ExternalLink size={10} /> doc
                  </a>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Primitives ──────────────────────────────────────────────
function ToggleCard({ active, onClick, children }) {
  return (
    <button onClick={onClick}
      style={{
        padding: '10px 14px', borderRadius: 10, fontSize: 13.5, fontWeight: 500, textAlign: 'left',
        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
        background: active ? 'var(--navy)' : '#fff',
        color: active ? '#fff' : 'var(--text-primary)',
        border: `1.5px solid ${active ? 'var(--navy)' : 'var(--border)'}`,
      }}>
      <span style={{
        width: 15, height: 15, borderRadius: 4, flexShrink: 0,
        border: `1.5px solid ${active ? '#fff' : 'var(--border)'}`,
        background: active ? 'var(--gold)' : 'transparent',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#fff', fontSize: 10,
      }}>
        {active && '✓'}
      </span>
      {children}
    </button>
  )
}

function ToggleChip({ active, onClick, children }) {
  return (
    <button onClick={onClick}
      style={{
        padding: '5px 12px', borderRadius: 50, fontSize: 12.5, fontWeight: 500, cursor: 'pointer',
        background: active ? 'var(--gold)' : 'var(--cream-dark)',
        color: active ? '#fff' : 'var(--text-secondary)',
        border: 'none',
      }}>{children}</button>
  )
}